import React, { useState } from "react";
import {
  Alert, Box, Breadcrumbs, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, Link as MLink, Stack, TextField, Typography,
} from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  useGetSessionByIdQuery, useSessionApproveMutation, useSessionReturnMutation,
  useSessionVoidMutation, useSessionRestoreMutation, useSessionSubmitReviewMutation,
} from "../../../Api/attoDeskApi";
import { PageSurface } from "../../../theme/b13Tokens";
import { useNotifier } from "../../../Core/Notifier";
import SessionEntryLog from "../shared/SessionEntryLog";

export default function SessionReview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const { data, refetch } = useGetSessionByIdQuery(id!, { skip: !id });
  const [approve] = useSessionApproveMutation();
  const [returnFor] = useSessionReturnMutation();
  const [voidSession] = useSessionVoidMutation();
  const [restore] = useSessionRestoreMutation();
  const [submitReview] = useSessionSubmitReviewMutation();

  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [voidReason, setVoidReason] = useState("");
  const [returnOpen, setReturnOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);

  const bundle: any = data?.data;
  const s = bundle?.session;
  const entries = bundle?.entries || [];
  const summary = bundle?.summary || {};

  if (!s) return <Box sx={{ p: 4, textAlign: "center" }}>Loading…</Box>;

  const call = async (fn: () => Promise<any>, ok: string) => {
    try {
      const r = await fn();
      if (r?.status) { showMessage(r.message || ok); refetch(); }
      else showErrorMessage(r?.message || "Failed");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  // Single source of truth: backend attaches the legal to-statuses for
  // THIS session's current status via SessionStatusTransitionService.
  // FE holds NO map — adding a status server-side flows through here
  // automatically, no drift possible. See show() in SessionDataController.
  const legal: string[] = Array.isArray(s.legalTransitions) ? s.legalTransitions : [];
  const canSubmitReview = legal.includes("awaiting_review");
  const canApprove      = legal.includes("reviewed");
  const canReturn       = legal.includes("returned_for_correction");
  const canVoid         = legal.includes("voided");
  const canRestore      = s.status === "voided" && legal.length > 0; // restore paths back to draft/completed/reviewed
  const hasAnyAction    = canSubmitReview || canApprove || canReturn || canVoid || canRestore;

  return (
    <Box sx={{ ...PageSurface, p: 2 }}>
      <Breadcrumbs sx={{ mb: 1 }}>
        <MLink underline="hover" color="inherit" component={Link} to="/aba-therapy/programs">ABA Therapy</MLink>
        <MLink underline="hover" color="inherit" component={Link} to="/aba-therapy/sessions">Session Data</MLink>
        <MLink underline="hover" color="inherit" component={Link} to={`/aba-therapy/sessions/${id}`}>Session #{id}</MLink>
        <Typography color="text.primary">Review</Typography>
      </Breadcrumbs>

      <Card sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ md: "center" }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Review Session #{s.id}</Typography>
            <Typography variant="caption" color="text.secondary">{s.studentName} · {s.programName} · {s.targetName} · {s.sessionDate}</Typography>
          </Box>
          <Chip label={s.status} color={s.status === "reviewed" ? "success" : s.status === "voided" ? "default" : "info"} />
        </Stack>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Metrics</Typography>
            {summary.trial?.completed > 0 && <Typography variant="body2">Trial-Based: {summary.trial.correct}/{summary.trial.denominator} correct — <b>{summary.trial.percentCorrect}%</b></Typography>}
            {summary.frequency?.count > 0 && <Typography variant="body2">Frequency: {summary.frequency.count} events · {summary.frequency.ratePerMinute}/min</Typography>}
            {summary.duration?.episodes > 0 && <Typography variant="body2">Duration: {summary.duration.episodes} eps · total {summary.duration.totalSeconds}s</Typography>}
            {summary.latency?.attempts > 0 && <Typography variant="body2">Latency: {summary.latency.attempts} attempts · avg {summary.latency.averageMs}ms</Typography>}
            {summary.interval?.completed > 0 && <Typography variant="body2">Interval: {summary.interval.completed} recorded · present {summary.interval.percentPresent}%</Typography>}
            {summary.task?.stepsCompleted > 0 && <Typography variant="body2">Task steps: {summary.task.stepsCompleted} · indep {summary.task.percentIndependent}%</Typography>}
            {summary.abc?.events > 0 && <Typography variant="body2">ABC events: {summary.abc.events}</Typography>}
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Entries ({entries.length})</Typography>
            <SessionEntryLog entries={entries} readOnly />
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ p: 2, mt: 2 }}>
        {!hasAnyAction ? (
          // Zero-legal-actions state — say so plainly rather than an
          // empty footer. Reviewed sessions land here with no action
          // (Void is permission-gated and not enabled here).
          <Alert severity="info">
            {s.status === "reviewed"
              ? "This session is reviewed — no further action from this page."
              : s.status === "voided"
              ? "This session is voided — no active actions available."
              : `This session is ${s.status} — no actions available from this state.`}
          </Alert>
        ) : (
          <>
            {/* Note field only relevant for Approve. Kept above the
               action row so it's not orphaned when Approve isn't
               offered — but hidden when there's no note-taking action
               available (submit-review / return / void don't use it). */}
            {canApprove && (
              <TextField fullWidth size="small" label="Review note (optional)" value={note}
                onChange={(e) => setNote(e.target.value)} sx={{ mb: 1 }} />
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end"
              sx={{ pr: { xs: 0, md: "80px" }, pb: { xs: "80px", md: 1 } }}>
              {/* Actions absent (not disabled) when illegal — same rule as the
                 locked program's write controls. */}
              {canSubmitReview && (
                <Button variant="contained" color="primary"
                  onClick={() => call(() => submitReview(id!).unwrap(), "Submitted for review")}>
                  Submit for Review
                </Button>
              )}
              {canReturn && (
                <Button variant="outlined" color="warning"
                  onClick={() => setReturnOpen(true)}>
                  Return for correction
                </Button>
              )}
              {canVoid && (
                <Button variant="outlined" color="error"
                  onClick={() => setVoidOpen(true)}>
                  Void
                </Button>
              )}
              {canApprove && (
                <Button variant="contained" color="success"
                  onClick={() => call(() => approve({ id: id!, note }).unwrap(), "Approved")}>
                  Approve
                </Button>
              )}
              {canRestore && (
                <Button variant="outlined"
                  onClick={() => call(() => restore({ id: id!, toStatus: "completed" }).unwrap(), "Restored")}>
                  Restore to Completed
                </Button>
              )}
            </Stack>
          </>
        )}
      </Card>

      <Dialog open={returnOpen} onClose={() => setReturnOpen(false)}>
        <DialogTitle>Return for correction</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline minRows={3} label="Correction reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!reason.trim()}
            onClick={() => { call(() => returnFor({ id: id!, reason }).unwrap(), "Returned"); setReturnOpen(false); }}>
            Return
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={voidOpen} onClose={() => setVoidOpen(false)}>
        <DialogTitle>Void session</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>Voiding preserves entries and audit history but excludes the session from progress calculations.</Alert>
          <TextField autoFocus fullWidth multiline minRows={3} label="Void reason (required)" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={!voidReason.trim()}
            onClick={() => { call(() => voidSession({ id: id!, reason: voidReason }).unwrap(), "Voided"); setVoidOpen(false); }}>
            Confirm Void
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
